<template>
  <div>
    <div class="d-flex align-items-end pt-2 pb-6">
      <div class="d-flex flex-column">
        <h1 class="h5 mb-2">
          Notifications
        </h1>
        <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
          <b-breadcrumb-item :to="{ name: 'Dashboard' }">
            <i class="fa fa-home fa-sm mr-2"></i>
            Home
          </b-breadcrumb-item>
          <b-breadcrumb-item active>Notifications</b-breadcrumb-item>
        </b-breadcrumb>
      </div>
    </div>

    <b-card
      no-body
      class="card-custom card-border">

      <b-card-body>
        <template v-if="!eventsElementsEmpty">
          <!--begin::Toolbar-->
          <div class="d-flex justify-content-between flex-wrap mb-3">
            <b-button-toolbar
              key-nav
              aria-label="Notifications Toolbar"
              class="mb-5">
              <b-button-group class="bg-light rounded border px-2">
                <b-button
                  variant="link-primary"
                  class="mr-2 btn-icon"
                  @click="handleMultiSelection()">
                  <template v-if="allEventsSelected && !noEventsSelected">
                    <i class="far fa-check-square"></i>
                  </template>
                  <template v-if="noEventsSelected && !allEventsSelected">
                    <i class="far fa-square"></i>
                  </template>
                  <template v-if="someEventsSelected && !allEventsSelected && !noEventsSelected">
                    <i class="far fa-minus-square"></i>
                  </template>
                </b-button>
                <b-button
                  :disabled="!someEventsSelected"
                  variant="link-primary"
                  class="btn-icon"
                  @click="deleteEvents()">
                  <i class="fa fa-trash fa-sm"></i>
                </b-button>
              </b-button-group>
            </b-button-toolbar>
            <b-button
              variant="outline-danger"
              class="mb-5"
              @click="showModal('confirmDelete')">
              <i class="fa fa-trash fa-sm mr-1"></i>
              <span>Delete All</span>
            </b-button>
          </div>
          <!--end::Toolbar-->
          <b-card
            v-for="(event, i) in eventsList"
            v-bind:key="i"
            body-class="px-4 py-3 px-lg-8 py-lg-6"
            :body-bg-variant="eventSelected(event) ? 'light' : ''"
            class="mb-4 cursor-pointer no-select notification-card"
            :class="eventSelected(event) ? 'selected' : ''"
            @click="selectEvent(event)">
            <!--begin::Item-->
            <div
              class="d-flex align-items-start align-items-sm-center flex-wrap flex-column flex-sm-row"
              v-bind:key="i">
              <!--begin::Checkbox-->
              <label
                class="checkbox checkbox-lg checkbox-single checkbox-light-primary flex-shrink-0 m-0 mr-4 mr-lg-6 mb-3 mb-sm-0 no-events">
                <input
                  type="checkbox"
                  name=""
                  value="1"
                  :checked="eventSelected(event)" />
                <span></span>
              </label>
              <!--end::Checkbox-->
              <!--begin::Text-->
              <div class="d-flex flex-column flex-grow-1">
                <a
                  class="text-dark-75 font-weight-bold font-size-lg mb-1">
                  {{ event.message }}
                </a>
                <span class="d-flex align-items-center mt-1">
                  <span class="text-muted font-weight-bold">
                    {{ event.created | moment("DD.MM.YYYY, HH:mm") }}
                  </span>
                  <span class="label label-dot label-success mx-3"></span>
                  <span class="text-muted font-weight-bold font-size-sm">
                    {{ event.created | timeAgo }}
                  </span>
                </span>
              </div>
              <!--end::Text-->
              <!--begin::Tags-->
              <div class="d-flex mt-4 mt-sm-0">
                <b-badge
                  v-for="(tag, index) in event.tags"
                  :key="`${event._id}-tag-${index}`"
                  variant="light-info"
                  :class="{ 'ml-2' : index > 0 }">{{ tag }}</b-badge>
              </div>
              <!--end:Tags-->
            </div>
            <!--end:Item-->
          </b-card>
          <!--begin::Load More-->
          <b-button
            v-if="nextPageAvailable"
            variant="outline-primary"
            class="d-block mx-auto mt-7"
            :disabled="eventsResponses('getEventsList')"
            @click="getEventsList({ type: 'next' })">
            <template v-if="eventsResponses('getEventsList')">
              <span>Please Wait...</span>
            </template>
            <template v-else>
              <i class="fa fa-plus fa-sm"></i>
              <span>Load More</span>
            </template>
          </b-button>
          <!--end:Load More-->
        </template>
        <template v-else>
          <no-data-found :text="'No New Notifications'" />
        </template>
      </b-card-body>
    </b-card>

    <!-- delete confirmation -->
    <b-modal
      ref="confirmDelete"
      centered
      size="md"
      title="Wait! Are you sure?"
      header-text-variant="danger"
      hide-header-close
      scrollable>

      <p class="font-size-lg mb-0">By confirming this action, all notifications (<span class="font-weight-bolder">{{ totalEventsElements }}</span>), will be permanently deleted from the system, and you will no longer have access to them.</p>

      <template #modal-footer>
        <div class="d-flex w-100">
          <b-button
            variant="outline-secondary"
            size="md"
            @click="hideModal('confirmDelete')">
            Cancel
          </b-button>
          <b-button
            variant="danger"
            size="md"
            class="ml-auto"
            @click="deleteAllEvents(), hideModal('confirmDelete')">
            Yes, delete all
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  // actions
  GET_EVENTS_LIST,
  SELECT_ALL_EVENTS,
  DELETE_EVENTS,
  DELETE_ALL_EVENTS,

  // mutations
  CLEAR_EVENTS_LIST,
  CLEAR_EVENTS_PAGES,
  SELECT_EVENT,
  DESELECT_ALL_EVENTS,
} from '@/core/services/store/notifications.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import { timeAgo } from '@/core/filters'
import { showToast, showModal, hideModal } from '@/core/utils'

export default {
  name: 'Notifications',
  filters: {
    timeAgo
  },
  data() {
    return {
      showToast,
      showModal,
      hideModal,
    }
  },
  computed: {
    ...mapGetters([
      'totalEventsElements',
      'eventsElementsEmpty',
      'eventsList',
      'nextPageAvailable',
      'noEventsSelected',
      'someEventsSelected',
      'allEventsSelected',
      'selectedEventsLength',
      'eventSelected',
      'eventsResponses',
    ]),
  },
  beforeDestroy() {
    this.$store.commit(CLEAR_EVENTS_LIST)
    this.$store.commit(CLEAR_EVENTS_PAGES)

    eventBus.$emit('clearNotificationsInterval')
    eventBus.$emit('restartNotificationsTimer')

    eventBus.$off('showToast/Notifications')
  },
  created() {
    this.getEventsList({ type: 'refresh' })
  },
  mounted() {
    eventBus.$on('showToast/Notifications', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  methods: {
    getEventsList({ type }) {
      this.$store.dispatch(GET_EVENTS_LIST, { type })
    },

    selectEvent(event) {
      this.$store.commit(SELECT_EVENT, event)
    },

    deleteEvents() {
      this.$store.dispatch(DELETE_EVENTS)
    },

    deleteAllEvents() {
      this.$store.dispatch(DELETE_ALL_EVENTS)
    },

    handleMultiSelection() {
      const {
        $store,
        allEventsSelected,
        noEventsSelected,
        someEventsSelected
      } = this

      if (allEventsSelected) {
        $store.commit(DESELECT_ALL_EVENTS)
      } else if (noEventsSelected) {
        $store.dispatch(SELECT_ALL_EVENTS)
      } else if (someEventsSelected) {
        $store.commit(DESELECT_ALL_EVENTS)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.notification-card {
  transition: all .3s ease;

  &:not(.selected) {
    &:hover {
      border-color: #dddfe5;
    }
  }
}
</style>
